import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import { MapContainer, TileLayer, Polyline, useMapEvents } from "react-leaflet";
import Supercluster from "supercluster";

import { IMapZoomState, initialMapZoomState } from "./interfaces/IMapZoomState";
import { ILeafletClusterState, initialLeafletClusterState } from "./interfaces/ILeafletClusterState";
import * as Utils from "../../utils";
import { usePrevious } from "../../utils/usePrevious";
import { Logger } from "../../utils/Logger";
import { Device } from "../../models";
import { TPin , TLocationProperty } from "../../types";
import { ISettings } from "../../interfaces";
import { Sendum } from "../../Sendum";
import { EventEmitter } from "../../services";

import { defaultIcon, getDefaultPinIcon, leafletMap } from "./utils";
import { LMarker } from "./LMarker";
import ZoomControl from "./ZoomControl";
import DeviceMapPopup from "./DeviceMapPopup";
import FullscreenControl from "./FullscreenControl";
import InfoControl from "./InfoControl";
import ClusterMapPopup from "./ClusterMapPopup";
import { TClusterPointProperties } from "./TClusterPointProperties";

import s from "./index.module.scss";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("SendumMap");

interface IProps {
	className?: string;
	device?: Device;
	location?: TLocationProperty;
	pins?: TPin[];
	isHistoryPins?: boolean;
	onClick?: (device: Device) => void;
	fullscreen?: boolean;
	isLoading?: boolean;
	fullscreenRef?: React.RefObject<HTMLDivElement>;
	widget?: "Map" | "Visibility" | "Selector";
	clusterPopupOn?: boolean;
	children?: JSX.Element | JSX.Element[];
}

export const SendumMap = forwardRef(({
	className,
	isLoading,
	device,
	location,
	pins,
	isHistoryPins,
	onClick,
	fullscreen,
	fullscreenRef,
	widget,
	clusterPopupOn,
	children,
}: IProps, ref) => {
	const settings: ISettings = Sendum.getSettings();

	const mapRef = useRef<L.Map>(null);
	const markerRef = useRef<L.Marker>();
	const [mapZoomState, setMapZoomState] = useState<IMapZoomState>(initialMapZoomState);
	const [leafletClusterState, setLeafletClusterState] = useState<ILeafletClusterState>(initialLeafletClusterState);
	const [fitBoundEnabled, setFitBoundEnabled] = useState<boolean>(true);
	const popUp = useRef<number | undefined>();

	const supercluster = useRef<Supercluster>();
	const onViewportChangedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevDevice = usePrevious<Device | undefined>(device);

	useImperativeHandle(ref, () => ({
		resetFitBounds() {
			setFitBoundEnabled(true);
			leafletMapFitBound();
		},
	}));

	const leafletMapFitBound = useCallback((map?: L.Map): void => {
		if (map) {
			try {
				const minZoom = Math.max(0, map.getBoundsZoom(leafletMap.worldViewBounds) - leafletMap.zoomSnap);
				map.setMinZoom(minZoom);
				setMapZoomState({
					zoom: map.getZoom(),
					minZoom: map.getMinZoom(),
					maxZoom: map.getMaxZoom(),
				});
				map.invalidateSize();
				const _location = location ?? device?.getLocation();
				if (!_location && (!pins || pins.length === 0)) {
					map.fitBounds(leafletMap.worldViewBounds, { animate: false, noMoveStart: true });

					return;
				}

				const bounds = getBounds(pins);
				if (_location) {
					bounds.extend([_location.latitude, _location.longitude]);
				}
				const circle = L.circle(bounds.getCenter(), { radius: 5000, color: "rgba(0,0,0,0)" });
				circle.addTo(map);
				bounds.extend(circle.getBounds().getNorthEast());
				bounds.extend(circle.getBounds().getSouthWest());

				setTimeout(() => {
					try {
						map.fitBounds(bounds, {
							animate: true,
							noMoveStart: true,
							paddingTopLeft: [widget === "Map" ? 360 : 50, 50],
							paddingBottomRight: [50, 50],
						});
					} catch (ignore) {
						logger.debug(ignore);
					}
				}, 1000);

				circle.removeFrom(map);
			} catch (e) {
				logger.error(e);
			}
		}
	}, [location, device, pins, widget]);

	const mapFitBounds = useCallback((): void => {
		if (fitBoundEnabled && mapRef.current) {
			leafletMapFitBound(mapRef.current);
		}
	}, [fitBoundEnabled, leafletMapFitBound]);

	React.useEffect(() => () => {
		window.removeEventListener("resize", mapFitBounds);
	}, [mapFitBounds]);

	const moveToPin = useCallback((epoch: number) => {
		if (fitBoundEnabled) {
			setFitBoundEnabled(false);
		}
		const pinsAround = pins?.filter(p => p.epoch && p.epoch >= epoch - 5 * 60 * 1000 && p.epoch <= epoch + 5 * 60 * 1000)
			.map(p => [Math.abs((p.epoch || 0) - epoch), p]);

		if (!!pinsAround && pinsAround.length) {
			const pin = pinsAround.reduce((min, p) => (min[0] > p[0] ? p : min), pinsAround[0])[1] as TPin;
			if (pin) {
				popUp.current = pin.epoch;
				const map = mapRef.current;
				if (map) {
					map.panTo(pin.position);
				}
			}
		}
	}, [pins, popUp, fitBoundEnabled]);

	React.useEffect(() => {
		EventEmitter.on("map", moveToPin);

		return () => {
			EventEmitter.off("map", moveToPin);
		};
	}, [moveToPin]);

	const addHoverPin = React.useCallback(({ lat, lng }: { lat: number; lng: number }) => {
		const marker: L.Marker = new L.Marker([lat, lng], { icon: defaultIcon });
		const map = mapRef.current;
		if (map) {
			map.addLayer(marker);
			if (markerRef.current) {
				map.removeLayer(markerRef.current);
			}
			markerRef.current = marker;
		}

	}, [markerRef, mapRef]);

	React.useEffect(() => {
		EventEmitter.on("data_table_map", addHoverPin);

		return () => {
			EventEmitter.off("data_table_map", addHoverPin);
		};
	}, [addHoverPin]);

	React.useEffect(() => {
		if (isLoading === false) {
			if (mapRef.current) {
				window.addEventListener("resize", mapFitBounds);
			}
			mapFitBounds();
		}
	}, [mapFitBounds, isLoading]);

	React.useEffect(() => {
		mapFitBounds();
		if (prevDevice?.id !== device?.id) {
			setFitBoundEnabled(true);
			leafletMapFitBound();
		}
	}, [device, leafletMapFitBound, mapFitBounds, pins, prevDevice?.id]);

	const isClusterFeature = (point: Supercluster.ClusterFeature<Supercluster.AnyProps>): boolean => (point && point.properties && point.properties.cluster);

	const onClickCluster = useCallback((anchor: number[], clusterId: number): boolean => {
		if (!supercluster.current) {
			return false;
		}
		let newZoom = supercluster.current.getClusterExpansionZoom(clusterId);
		if (mapRef) {
			const map = mapRef.current;
			if (map && newZoom > map.getMaxZoom()) {
				newZoom = map.getMaxZoom();
			}
		}
		if (mapZoomState.zoom === newZoom) {
			return true;
		}
		setLeafletClusterState({
			...leafletClusterState,
			center: new L.LatLng(anchor[0], anchor[1]),
			zoom: newZoom,
		});
		const map = mapRef.current;
		if (map) {
			map.setZoom(newZoom);
			map.panTo(new L.LatLng(anchor[0], anchor[1]));
		}
		setMapZoomState({
			...mapZoomState,
			zoom: newZoom,
		});
		setFitBoundEnabled(false);

		return false;
	}, [leafletClusterState, mapZoomState]);

	const getDevice = useCallback((deviceId: string | undefined, shipmentId: string | undefined): Device | undefined => pins?.find(p => p.device && p.device?.id === deviceId && p.device.shipment && p.device.shipment.id === shipmentId)?.device,[pins]);

	const MarkerPopup = useCallback((properties: TClusterPointProperties): JSX.Element | null => {
		if (isHistoryPins && properties.epoch) {
			return (
				<div>{Utils.time.format(properties.epoch, "D MMMM YYYY HH:mm:ss")}</div>
			);
		}

		if (properties.deviceId && properties.shipmentId) {
			return (
				<DeviceMapPopup device={getDevice(properties.deviceId, properties.shipmentId)}
					onClick={onClick}/>
			);
		}

		return null;
	},[getDevice, isHistoryPins, onClick]);

	const clustersLayer = useMemo((): JSX.Element => {
		const ps: Supercluster.PointFeature<TClusterPointProperties>[] = !pins ? [] : pins.map((p, i) => ({
			type: "Feature",
			properties: {
				index: i,
				epoch: p.epoch,
				deviceId: p.device?.id,
				shipmentId: p.device?.shipment.id,
				icon: p.icon,
			},
			geometry: {
				type: "Point",
				coordinates: [p.position.lng, p.position.lat],
			},
		}));

		supercluster.current = new Supercluster<TClusterPointProperties>({ radius: 50, maxZoom: 18 }).load(ps);

		return (<>
			{supercluster.current.getClusters(leafletClusterState.bbox, Math.round(leafletClusterState.zoom))
				.map((place: Supercluster.ClusterFeature<TClusterPointProperties>, index: number) => {
					const anchor = place.geometry.coordinates;

					if (isClusterFeature(place)) {

						const clusterId = (place).properties.cluster_id;

						const devices = supercluster.current?.getLeaves(clusterId).map((marker: Supercluster.PointFeature<TClusterPointProperties>) => getDevice(marker.properties.deviceId, marker.properties.shipmentId));
						let isHighlight = false;

						const leaves = supercluster.current?.getLeaves(place.properties.cluster_id, Infinity);
						if (leaves && popUp.current) {
							const l = leaves.find(layer => !!popUp.current && layer.properties.epoch === popUp.current);
							if (l) {
								isHighlight = true;
							}
						}

						return (
							<LMarker key={index} position={[anchor[1], anchor[0]]}
								count={(place).properties.point_count}
								isHighlight={isHighlight}
								onClick={() => onClickCluster([anchor[1], anchor[0]], clusterId)}>
								{clusterPopupOn && (<ClusterMapPopup devices={devices} onClick={onClick}/>)}
							</LMarker>
						);
					}

					return (
						<LMarker
							key={index}
							position={[anchor[1], anchor[0]]}
							icon={place.properties.icon as L.DivIcon}
							popupClass={isHistoryPins ? s.pointLeafletPopup : s.leafletPopup}
							isHighlight={!!popUp.current && popUp.current === place.properties.epoch}
							onClick={() => onClickMarkerHandler(place.properties.epoch)}
						>
							<MarkerPopup {...place.properties} />
						</LMarker>
					);
				})}
		</>);
	},[MarkerPopup, clusterPopupOn, getDevice, isHistoryPins, leafletClusterState.bbox, leafletClusterState.zoom, onClick, onClickCluster, pins]);

	const onClickMarkerHandler = (epoch?: number): boolean => {
		EventEmitter.emit("chart", epoch);

		return true;
	};

	const getBounds = (_pins?: TPin[]): L.LatLngBounds => {
		if (_pins?.length) {
			return new L.LatLngBounds(_pins.map(p => [p.position.lat, p.position.lng]));
		}

		return new L.LatLngBounds([]);

	};

	const zoomIn = (): void => {
		if (mapRef && mapZoomState.zoom < mapZoomState.maxZoom) {
			const map = mapRef.current;
			if (map) {
				setMapZoomState({
					...mapZoomState,
					...{ zoom: map.getZoom() + leafletMap.zoomSnap },
				});
				map.zoomIn();
				setFitBoundEnabled(false);
			}
		}
	};

	const zoomOut = (): void => {
		if (mapRef && mapZoomState.zoom > mapZoomState.minZoom) {
			const map = mapRef.current;
			if (map) {
				setMapZoomState({
					...mapZoomState,
					...{ zoom: map.getZoom() - leafletMap.zoomSnap },
				});
				map.zoomOut();
				setFitBoundEnabled(false);
			}
		}
	};

	const onViewportChangedHandler = (): void => {
		if (onViewportChangedTimer.current) {
			clearTimeout(onViewportChangedTimer.current);
			onViewportChangedTimer.current = null;
		}

		onViewportChangedTimer.current = setTimeout(() => {
			try {
				if (mapRef) {
					const map = mapRef.current;
					if (map) {
						const mapBounds = map.getBounds();
						setLeafletClusterState({
							center: map.getCenter(),
							zoom: map.getZoom(),
							bbox: [mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()],
						});
						setMapZoomState({
							zoom: map.getZoom(),
							minZoom: map.getMinZoom(),
							maxZoom: map.getMaxZoom(),
						});
					}
				}
			} catch (ignore) {
				logger.debug(ignore);
			}

			if (onViewportChangedTimer.current) {
				clearTimeout(onViewportChangedTimer.current);
				onViewportChangedTimer.current = null;
			}
		}, 500);
	};

	const tileLayer = (): JSX.Element => {
		if (settings?.map === "google" && settings?.googleMapKey) {
			if (window.google) {
				return (
					<TileLayer
						url="https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
						subdomains={["mt0", "mt1", "mt2", "mt3"]}
						noWrap={true}/>
				);
			}

			return (<div/>);
		}

		return (
			<TileLayer
				url={"https://{s}.tile.osm.org/{z}/{x}/{y}.png"}
				noWrap={true}/>
		);
	};

	const route = (): JSX.Element | null => {
		if (isHistoryPins && !!pins?.length) {
			return (
				<Polyline positions={pins.map(p => p.position)} weight={2} color={"#3387ff"} opacity={1.0}/>);
		}

		return null;
	};

	const devicePin = (): JSX.Element | null => {
		const _location = location ?? device?.getLocation();
		if (_location && isLoading === false) {
			return (
				<LMarker position={[_location.latitude, _location.longitude]}
					icon={getDefaultPinIcon(location, device)} popupClass={s.deviceLeafletPopup}>
					<div>{_location.epoch ? Utils.time.format(_location.epoch, "YYYY-MMM-D HH:mm:ss") : ""}</div>
				</LMarker>);
		}

		return null;
	};

	const MyComponent = (): JSX.Element | null => {
		useMapEvents({
			zoomend: onViewportChangedHandler,
			moveend: onViewportChangedHandler,
		});

		return null;
	};

	return (
		<div className={className} data-testid="shipment-track-map">
			{children}
			{isLoading && (
				<div className={s.loader}>
                    Loading...
				</div>
			)}
			<MapContainer
				ref={mapRef}
				center={leafletClusterState.center}
				zoom={leafletClusterState.zoom}
				zoomSnap={leafletMap.zoomSnap}
				zoomControl={false}
				scrollWheelZoom={false}
				style={{ width: "100%", height: "100%" }}
				maxBounds={leafletMap.maxBounds}
			>
				<MyComponent />
				<ZoomControl mapZoomState={mapZoomState} zoomIn={zoomIn} zoomOut={zoomOut}/>
				{(fullscreen && fullscreenRef) && (<FullscreenControl fullscreenRef={fullscreenRef}/>)}
				{fullscreen && (<InfoControl/>)}
				{tileLayer()}
				{clustersLayer}
				{route()}
				{devicePin()}
			</MapContainer>
		</div>
	);
});
